import type {PdfInkAnnotation} from '@reados/contracts';

export type InkPoint=readonly [number,number];

function pointDistance(point:InkPoint,a:InkPoint,b:InkPoint){
 const dx=b[0]-a[0],dy=b[1]-a[1],length=dx*dx+dy*dy;
 const t=length?Math.max(0,Math.min(1,((point[0]-a[0])*dx+(point[1]-a[1])*dy)/length)):0;
 return Math.hypot(point[0]-a[0]-t*dx,point[1]-a[1]-t*dy);
}
function segmentDistance(a:InkPoint,b:InkPoint,c:InkPoint,d:InkPoint){
 const cross=(p:InkPoint,q:InkPoint,r:InkPoint)=>(q[0]-p[0])*(r[1]-p[1])-(q[1]-p[1])*(r[0]-p[0]);
 const abC=cross(a,b,c),abD=cross(a,b,d),cdA=cross(c,d,a),cdB=cross(c,d,b);
 if(abC*abD<0&&cdA*cdB<0)return 0;
 return Math.min(pointDistance(a,c,d),pointDistance(b,c,d),pointDistance(c,a,b),pointDistance(d,a,b));
}
const middle=(a:InkPoint,b:InkPoint):InkPoint=>[(a[0]+b[0])/2,(a[1]+b[1])/2];
function cubicHit(a:InkPoint,b:InkPoint,c:InkPoint,d:InkPoint,from:InkPoint,to:InkPoint,radius:number,depth=0):boolean{
 // A conservative control-point bound rejects distant curves; the actual curve,
 // not its editor rectangle or sampled input polygon, determines an erasure.
 const xs=[a[0],b[0],c[0],d[0]],ys=[a[1],b[1],c[1],d[1]];
 if(Math.max(...xs)+radius<Math.min(from[0],to[0])||Math.min(...xs)-radius>Math.max(from[0],to[0])||Math.max(...ys)+radius<Math.min(from[1],to[1])||Math.min(...ys)-radius>Math.max(from[1],to[1]))return false;
 const flatness=Math.max(pointDistance(b,a,d),pointDistance(c,a,d));
 if(flatness<=Math.max(0.02,radius/16)||depth===14)return segmentDistance(a,d,from,to)<=radius+flatness;
 const ab=middle(a,b),bc=middle(b,c),cd=middle(c,d),abc=middle(ab,bc),bcd=middle(bc,cd),mid=middle(abc,bcd);
 return cubicHit(a,ab,abc,mid,from,to,radius,depth+1)||cubicHit(mid,bcd,cd,d,from,to,radius,depth+1);
}
export function inkPathHit(line:readonly (number|null)[],from:InkPoint,to:InkPoint,radius:number):boolean{
 let previous:InkPoint=[line[4]!,line[5]!];
 if(line.length===6)return pointDistance(previous,from,to)<=radius;
 for(let index=6;index<line.length;index+=6){
  const end:InkPoint=[line[index+4]!,line[index+5]!];
  if(line[index]===null){if(segmentDistance(previous,end,from,to)<=radius)return true;}
  else if(cubicHit(previous,[line[index]!,line[index+1]!],[line[index+2]!,line[index+3]!],end,from,to,radius))return true;
  previous=end;
 }
 return false;
}
/** Whole-stroke erasure: every path is independent, including older multi-path records. */
export function eraseInkPaths(annotation:PdfInkAnnotation,from:InkPoint,to:InkPoint,radius:number):PdfInkAnnotation|null{
 const keep=annotation.paths.lines.map((line,index)=>inkPathHit(line,from,to,radius+annotation.thickness/2)?-1:index).filter(index=>index>=0);
 if(keep.length===annotation.paths.lines.length)return annotation;
 if(!keep.length)return null;
 return {...annotation,paths:{lines:keep.map(index=>annotation.paths.lines[index]),points:keep.map(index=>annotation.paths.points[index])}};
}
